// Gestion des comptes, réservée aux administrateurs. Fonctionnelle de
// bout en bout : elle crée, modifie et désactive réellement.
//
// Chez AGC, `requireRole('admin')` ne protégeait que 3 routes sur 80 et
// cet écran n'existait pas du tout.

import { useCallback, useState } from 'react';
import { Card, CardHead, CardBody } from '../../ui/Card.jsx';
import Table from '../../ui/Table.jsx';
import Badge from '../../ui/Badge.jsx';
import Button from '../../ui/Button.jsx';
import Modal from '../../ui/Modal.jsx';
import { TextField, Select } from '../../ui/TextField.jsx';
import { EmptyState, ErrorState, SkeletonRows } from '../../ui/States.jsx';
import { useToast } from '../../ui/Toast.jsx';
import { useResource } from '../../lib/useResource.js';
import { useSession } from '../../lib/session.jsx';
import { api } from '../../lib/api.js';
import { formatDateTime } from '../../lib/format.js';

const EMPTY_FORM = { username: '', display_name: '', password: '', role: 'user' };

export function UtilisateursPage() {
  const toast = useToast();
  const { user: me } = useSession();
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [pending, setPending] = useState(false);

  const load = useCallback(() => api.get('/users'), []);
  const { data, error, loading, reload } = useResource(load);

  async function create(event) {
    event.preventDefault();
    setPending(true);
    setErrors({});
    try {
      await api.post('/users', form);
      toast.success(`Compte « ${form.username} » créé.`);
      setCreating(false);
      setForm(EMPTY_FORM);
      reload();
    } catch (err) {
      setErrors(err.details ?? { _: err.message });
    } finally {
      setPending(false);
    }
  }

  async function toggleActive(target) {
    const action = target.active ? 'deactivate' : 'activate';
    try {
      await api.post(`/users/${target.id}/${action}`);
      toast.success(target.active ? 'Compte désactivé.' : 'Compte réactivé.');
      reload();
    } catch (err) {
      toast.error(err.message);
    }
  }

  const columns = [
    { key: 'display_name', label: 'Nom' },
    { key: 'username', label: 'Identifiant' },
    {
      key: 'role',
      label: 'Rôle',
      render: (u) => (
        <Badge tone={u.role === 'admin' ? 'info' : 'neutral'}>
          {u.role === 'admin' ? 'Administrateur' : 'Deviseur'}
        </Badge>
      ),
    },
    {
      key: 'active',
      label: 'État',
      render: (u) => (
        <Badge tone={u.active ? 'ok' : 'neutral'}>{u.active ? 'Actif' : 'Désactivé'}</Badge>
      ),
    },
    { key: 'last_login_at', label: 'Dernière connexion', render: (u) => formatDateTime(u.last_login_at) },
    {
      key: 'actions',
      label: '',
      render: (u) =>
        // On ne se désactive pas soi-même : ce serait le moyen le plus
        // simple de se retrouver sans administrateur sur le serveur.
        u.id === me?.id ? null : (
          <Button size="sm" variant={u.active ? 'secondary' : 'ghost'} onClick={() => toggleActive(u)}>
            {u.active ? 'Désactiver' : 'Réactiver'}
          </Button>
        ),
    },
  ];

  return (
    <>
      <Card>
        <CardHead title="Comptes">
          <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
            Nouveau compte
          </Button>
        </CardHead>
        <CardBody>
          {loading ? (
            <SkeletonRows rows={3} />
          ) : error ? (
            <ErrorState error={error} onRetry={reload} />
          ) : (
            <Table
              columns={columns}
              rows={data?.users ?? []}
              caption="Liste des comptes utilisateurs"
              empty={<EmptyState title="Aucun compte">Créez le premier compte utilisateur.</EmptyState>}
            />
          )}
        </CardBody>
      </Card>

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="Nouveau compte"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreating(false)}>
              Annuler
            </Button>
            <Button variant="primary" form="form-nouveau-compte" type="submit" disabled={pending}>
              {pending ? 'Création…' : 'Créer'}
            </Button>
          </>
        }
      >
        <form id="form-nouveau-compte" className="stack" onSubmit={create}>
          <TextField
            label="Identifiant"
            required
            value={form.username}
            error={errors.username}
            hint="Lettres, chiffres, point, tiret et souligné."
            onChange={(e) => setForm({ ...form, username: e.target.value })}
          />
          <TextField
            label="Nom affiché"
            required
            value={form.display_name}
            error={errors.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
          />
          <TextField
            label="Mot de passe"
            type="password"
            autoComplete="new-password"
            required
            value={form.password}
            error={errors.password}
            hint="Douze caractères minimum, une minuscule, une majuscule, un chiffre."
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
          <Select
            label="Rôle"
            value={form.role}
            error={errors.role}
            options={[
              { value: 'user', label: 'Deviseur' },
              { value: 'admin', label: 'Administrateur' },
            ]}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
          />
          {errors._ ? (
            <p className="field__error" role="alert">
              {errors._}
            </p>
          ) : null}
        </form>
      </Modal>
    </>
  );
}

export default UtilisateursPage;
